import {BehaviorSubject, Observable} from "rxjs";


/**
 * This abstract class `AbstractLocalStorageService` provides a structured way to interact with the browser's localStorage.
 * It is meant to be extended by specific services that need to store and retrieve data of a certain type `T`.
 */
export abstract class AbstractLocalStorageService<T> {

  private subject: BehaviorSubject<T> = new BehaviorSubject<T>(this.get());

  /**
   * Retrieves the item from localStorage.
   * If the item is found, it is parsed from a JSON string to an object of type `T`.
   * If the item is not found, a default value is returned, as defined by the `getDefaultValue()` method.
   *
   * @returns The item from localStorage, parsed to type `T`, or the default value.
   */
  public get(): T {
    const item = localStorage.getItem(this.getItemKey());
    if (item) {
      return JSON.parse(item) as T;
    }
    return this.getDefaultValue();
  }

  /**
   * Возвращает Observable, на который можно подписаться,
   * чтобы получать обновления при изменении значения в localStorage.
   */
  public get$(): Observable<T> {
    return this.subject.asObservable();
  }

  /**
   * Saves the item to localStorage.
   * If the value is not provided (is falsy), the default value is used instead.
   * The item is saved in localStorage under the key obtained from the `getItemKey()` method.
   *
   * @param value - The value to be saved, of type `T`.
   */
  public save(value: T): void {
    if (!value) {
      value = this.getDefaultValue();
    }
    localStorage.setItem(this.getItemKey(), JSON.stringify(value));

    this.subject.next(value);
  }

  public clear(): void {
    localStorage.removeItem(this.getItemKey());
    this.subject.next(this.getDefaultValue());
  }

  /**
   * Abstract method to get the key under which the item is stored in localStorage.
   * Must be implemented by the subclass.
   *
   * @returns The localStorage key as a string.
   */
  protected abstract getItemKey(): string;

  /**
   * Abstract method to get the default value of the item.
   * Must be implemented by the subclass.
   *
   * @returns The default value of the item, of type `T`.
   */
  protected abstract getDefaultValue(): T;
}

/**
 * An abstract class that implements a call counter with a limit.
 * It's designed to track the number of times a local storage service is accessed,
 * ensuring the number of calls doesn't exceed a specified limit.
 */
export abstract class AbstractLocalStorageCallLimiterService extends AbstractLocalStorageService<number> {

  /**
   * Initializes a new instance of the service limiter with a specified limit.
   * @param limit The maximum number of allowed calls to the local storage service.
   */
  protected abstract getLimit(): number;

  /**
   * Checks if the number of calls made is still under the limit.
   * @returns {boolean} True if the number of calls is less than the limit, otherwise false.
   */
  public isWithinLimit(): boolean {
    return this.get() < this.getLimit();
  }

  public increment(): number {
    let counter = this.get() + 1;
    this.save(counter);
    return counter;
  }

  public get(): number {
    const item = localStorage.getItem(this.getItemKey());
    let counter = this.getDefaultValue();
    if (item) {
      counter = JSON.parse(item) as number;
    }
    return counter;
  }

  protected getDefaultValue(): number {
    return 0;
  }
}
